import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useStore } from "@/lib/useStore";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StorefrontLayout from "@/components/layouts/StorefrontLayout";
import { NotFound } from "@/pages/ErrorPages";
import { Skeleton } from "@/components/ui/skeleton";
import { formatMoney, intervalLabel } from "@/lib/money";
import { Check, Sparkles, ShieldCheck, Zap, ArrowRight, Clapperboard } from "lucide-react";

export default function StorefrontPlans() {
  const { slug, sp } = useStore();
  const navigate = useNavigate();
  const { user, status, logout } = useAuth();
  const [data, setData] = useState(null);
  const [pageStatus, setPageStatus] = useState("loading");
  const [interval, setInterval] = useState("monthly");

  useEffect(() => {
    api.get(`/public/tenants/${slug}/plans`)
      .then((r) => { setData(r.data); setPageStatus("ready"); })
      .catch(() => setPageStatus("not_found"));
  }, [slug]);

  const hasYearly = useMemo(() => (data?.plans || []).some((p) => p.interval === "yearly"), [data]);
  const hasMonthly = useMemo(() => (data?.plans || []).some((p) => p.interval === "monthly"), [data]);
  const visible = useMemo(() => {
    if (!data) return [];
    return data.plans.filter((p) => p.interval === interval && p.status !== "archived");
  }, [data, interval]);

  if (pageStatus === "not_found") return <NotFound />;

  const tenant = data?.tenant;
  const themeColor = tenant?.theme_color || "#7C3AED";
  const accentColor = tenant?.accent_color || "#F59E0B";
  const isAuthed = status === "authenticated" && !!user;

  return (
    <StorefrontLayout tenant={tenant || { name: "" }} currentUser={isAuthed ? user : null} onLogout={logout} showSubscriberNav={isAuthed} heroBleed>
      <section className="relative grain overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              `radial-gradient(55% 80% at 15% 0%, color-mix(in srgb, ${themeColor} 26%, transparent), transparent 68%),` +
              `radial-gradient(45% 70% at 90% 6%, color-mix(in srgb, ${accentColor} 16%, transparent), transparent 70%),` +
              `linear-gradient(180deg, #0b0b0c 0%, #0a0a0a 100%)`,
          }}
        />
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 pt-28 md:pt-36 pb-16 md:pb-24" data-testid="storefront-plans-page">
          {/* Hero */}
          <p className="text-[11px] uppercase tracking-[0.4em] text-white/40" data-testid="plans-hero">{tenant?.name} · Pricing</p>
          <h1 className="mt-3 font-display font-black tracking-tighter text-4xl md:text-6xl leading-[0.95]">
            Pick a plan,<br /><span style={{ color: accentColor }}>start watching.</span>
          </h1>
          <p className="mt-4 max-w-xl text-white/60 font-light">
            One subscription unlocks the full {tenant?.name} catalogue. Cancel any time.
          </p>

          {/* Trust row */}
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.2em] text-white/45">
            <span className="inline-flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" style={{ color: accentColor }} /> Instant access</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" style={{ color: accentColor }} /> Cancel anytime</span>
            <span className="inline-flex items-center gap-1.5"><Clapperboard className="w-3.5 h-3.5" style={{ color: accentColor }} /> Full catalogue</span>
          </div>

          {/* Interval toggle */}
          {(hasMonthly || hasYearly) && (
            <div className="mt-8 inline-flex p-1 bg-white/[0.04] ring-1 ring-white/12 rounded-full" data-testid="billing-cycle-toggle">
              <button
                onClick={() => setInterval("monthly")}
                className={`px-5 py-2 text-[11px] uppercase tracking-[0.22em] rounded-full transition-colors ${interval === "monthly" ? "text-black" : "text-white/60 hover:text-white"}`}
                style={interval === "monthly" ? { backgroundColor: accentColor } : {}}
                data-testid="billing-toggle-monthly"
              >
                Monthly
              </button>
              <button
                onClick={() => setInterval("yearly")}
                className={`px-5 py-2 text-[11px] uppercase tracking-[0.22em] rounded-full transition-colors ${interval === "yearly" ? "text-black" : "text-white/60 hover:text-white"}`}
                style={interval === "yearly" ? { backgroundColor: accentColor } : {}}
                data-testid="billing-toggle-yearly"
              >
                Yearly
              </button>
            </div>
          )}

          {/* Cards / states */}
          {pageStatus === "loading" ? (
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl" data-testid="plans-loading">
              <Skeleton className="h-80 w-full bg-white/10" />
              <Skeleton className="h-80 w-full bg-white/10" />
            </div>
          ) : visible.length === 0 ? (
            <div className="mt-12 max-w-2xl">
              <div className="relative overflow-hidden ring-1 ring-white/10 bg-[#101011] px-8 py-14 text-center" data-testid="empty-no-plans">
                <div className="absolute inset-0 -z-10 opacity-70" style={{ background: `radial-gradient(45% 120% at 50% 0%, color-mix(in srgb, ${themeColor} 16%, transparent), transparent 70%)` }} />
                <div className="w-12 h-12 mx-auto grid place-items-center rounded-full ring-1 ring-white/15 bg-white/5 mb-5">
                  <Sparkles className="w-6 h-6" style={{ color: accentColor }} />
                </div>
                <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">
                  {interval === "yearly" && hasMonthly ? "No yearly plan yet" : "Plans are on the way"}
                </h2>
                <p className="mt-3 text-white/55 text-sm max-w-md mx-auto">
                  {interval === "yearly" && hasMonthly
                    ? "This studio only offers a monthly plan right now. Switch to monthly to subscribe."
                    : `${tenant?.name || "This studio"} is putting the finishing touches on its subscriptions. Check back very soon.`}
                </p>
                <div className="mt-7 flex items-center justify-center gap-3">
                  {interval === "yearly" && hasMonthly ? (
                    <button onClick={() => setInterval("monthly")} className="inline-flex items-center gap-2 px-7 h-12 text-sm font-bold uppercase tracking-[0.2em] transition-transform hover:-translate-y-0.5" style={{ backgroundColor: accentColor, color: "#0a0a0a" }} data-testid="empty-no-plans-cta">
                      See monthly <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button onClick={() => navigate(sp(`/home`))} className="inline-flex items-center gap-2 px-7 h-12 text-sm font-bold uppercase tracking-[0.2em] bg-white text-black hover:bg-white/90 transition-colors" data-testid="empty-no-plans-cta">
                      <Clapperboard className="w-4 h-4" /> Browse catalogue
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl" data-testid="plans-grid">
              {visible.map((p, i) => {
                const best = visible.length > 1 && i === visible.length - 1;
                return (
                  <div
                    key={p.id}
                    className="relative bg-[#121212] p-6 md:p-7 transition-[box-shadow,transform] duration-200 hover:-translate-y-0.5"
                    style={{ boxShadow: best ? `inset 0 0 0 1.5px ${accentColor}` : "inset 0 0 0 1px rgba(255,255,255,0.10)" }}
                    data-testid={`plan-card-${p.id}`}
                  >
                    {best && (
                      <span className="absolute -top-2.5 left-6 px-2.5 py-1 text-[9px] uppercase tracking-[0.3em] font-bold" style={{ backgroundColor: accentColor, color: "#0a0a0a" }}>
                        Best value
                      </span>
                    )}
                    <h3 className="font-display font-bold text-2xl tracking-tight" data-testid={`plan-name-${p.id}`}>{p.name}</h3>
                    <div className="mt-3 flex items-baseline gap-1.5">
                      <span className="font-display font-black text-5xl">{formatMoney(p.price_amount, p.currency)}</span>
                      <span className="text-white/45 text-sm">{intervalLabel(p.interval)}</span>
                    </div>
                    {p.description && <p className="mt-3 text-sm text-white/55 font-light">{p.description}</p>}
                    <ul className="mt-5 space-y-2 text-sm text-white/75">
                      {(p.perks && p.perks.length ? p.perks : ["Full catalogue", "HD streaming", "Cancel anytime"]).map((perk, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: accentColor }} /> {perk}
                        </li>
                      ))}
                    </ul>
                    {p.trial_days > 0 && (
                      <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-white/45">{p.trial_days}-day free trial</p>
                    )}
                    <Link
                      to={isAuthed ? sp(`/checkout?type=sub&plan=${p.id}`) : sp(`/login?next=${encodeURIComponent(sp(`/checkout?type=sub&plan=${p.id}`))}`)}
                      className="mt-6 flex items-center justify-center gap-2 px-5 py-3.5 text-xs uppercase tracking-[0.25em] font-bold transition-transform hover:-translate-y-0.5"
                      style={best ? { backgroundColor: accentColor, color: "#0a0a0a" } : { backgroundColor: "#fff", color: "#0a0a0a" }}
                      data-testid={`plan-select-button-${p.id}`}
                    >
                      {isAuthed ? "Subscribe" : "Sign in & subscribe"} <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-10 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-white/35">
            <Sparkles className="w-3.5 h-3.5" /> Mock checkout — no real charge in MVP
          </div>
        </div>
      </section>
    </StorefrontLayout>
  );
}
